<template>
  <div class="esc-pedidos-page">
    <!-- Cabecera -->
    <div class="esc-header mb-2">
      <div class="d-flex align-items-center">
        <div class="esc-header__icon">
          <i class="fa-solid fa-clipboard-list"></i>
        </div>
        <div>
          <h2 class="mb-0 font-weight-bolder">Pedidos del escáner</h2>
          <small class="text-muted">Consulta y edita los pedidos guardados</small>
        </div>
      </div>
      <b-button variant="outline-primary" :to="{ name: 'escaner' }">
        <i class="fa-solid fa-barcode mr-50"></i> Nuevo pedido
      </b-button>
    </div>

    <!-- KPIs -->
    <div class="esc-kpis mb-2">
      <div class="esc-kpi">
        <span class="esc-kpi__label">Pedidos</span>
        <span class="esc-kpi__value">{{ pedidosActivos.length }}</span>
        <span class="esc-kpi__foot">{{ totalUnidadesKpi }} uds<template v-if="anioFiltro !== 'Todos'"> · {{ anioFiltro }}</template></span>
      </div>
      <div class="esc-kpi">
        <span class="esc-kpi__label">Total WSP</span>
        <span class="esc-kpi__value">{{ sumaEnEuros(p => p.total) }}</span>
        <span class="esc-kpi__foot">{{ sumaPorMoneda(p => p.total) || 'valor de los pedidos' }}</span>
      </div>
      <div class="esc-kpi esc-kpi--ok">
        <span class="esc-kpi__label">Cobrado</span>
        <span class="esc-kpi__value">{{ sumaEnEuros(p => p.pagado) }}</span>
        <span class="esc-kpi__foot">{{ sumaPorMoneda(p => p.pagado) || 'pagos registrados' }}</span>
      </div>
      <div class="esc-kpi esc-kpi--warn">
        <span class="esc-kpi__label">Pendiente de cobro</span>
        <span class="esc-kpi__value">{{ sumaEnEuros(p => Math.max(0, totalCobrar(p) - (Number(p.pagado) || 0))) }}</span>
        <span class="esc-kpi__foot">{{ sumaPorMoneda(p => Math.max(0, totalCobrar(p) - (Number(p.pagado) || 0))) || 'por cobrar' }}</span>
      </div>
      <div class="esc-kpi">
        <span class="esc-kpi__label">En proformas</span>
        <span class="esc-kpi__value">{{ sumaEnEuros(p => (p.proforma && p.proforma.importe) || 0) }}</span>
        <span class="esc-kpi__foot">{{ totalProformas }} proformas</span>
      </div>
    </div>

    <b-card no-body>
      <b-card-body>
        <div v-if="anios.length" class="esc-anios mb-1">
          <b-button
            size="sm"
            :variant="anioFiltro === 'Todos' ? 'primary' : 'outline-secondary'"
            pill
            @click="anioFiltro = 'Todos'"
          >
            Todos
          </b-button>
          <b-button
            v-for="a in anios"
            :key="a"
            size="sm"
            :variant="anioFiltro === a ? 'primary' : 'outline-secondary'"
            pill
            @click="anioFiltro = a"
          >
            {{ a }}
          </b-button>

          <span class="esc-anios__sep"></span>

          <b-button
            v-for="f in ['Todos', 'Por pagar', 'Pagados']"
            :key="'pago-' + f"
            size="sm"
            :variant="pagoFiltro === f ? (f === 'Por pagar' ? 'warning' : 'primary') : 'outline-secondary'"
            pill
            @click="pagoFiltro = f"
          >
            {{ f }}
          </b-button>

          <span class="esc-anios__sep"></span>

          <b-button
            v-for="f in ['Todos', 'Web', 'ERP']"
            :key="'origen-' + f"
            size="sm"
            :variant="origenFiltro === f ? (f === 'Web' ? 'info' : 'primary') : 'outline-secondary'"
            pill
            @click="origenFiltro = f"
          >
            <i v-if="f === 'Web'" class="fa-solid fa-globe mr-25"></i>{{ f }}
          </b-button>
        </div>

        <b-input-group class="mb-1">
          <b-input-group-prepend is-text>
            <i class="fa-solid fa-magnifying-glass text-muted"></i>
          </b-input-group-prepend>
          <b-form-input
            v-model="q"
            type="search"
            autocomplete="off"
            data-bwignore
            placeholder="Buscar por nombre o email…"
            @keyup.enter="cargar"
          />
          <b-input-group-append>
            <b-button variant="primary" @click="cargar">Buscar</b-button>
          </b-input-group-append>
        </b-input-group>

        <b-table
          :items="pedidosFiltrados"
          :fields="fields"
          :busy="cargando"
          :tbody-tr-class="filaClase"
          responsive
          show-empty
          empty-text="No hay pedidos guardados"
          class="mb-0"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-50" /> Cargando…
            </div>
          </template>

          <template #cell(toggle)="row">
            <b-button
              size="sm"
              variant="flat-secondary"
              class="btn-icon esc-toggle-btn"
              :class="{ active: row.detailsShowing }"
              :title="row.detailsShowing ? 'Ocultar artículos' : 'Ver artículos'"
              @click="row.toggleDetails"
            >
              <i class="fa-solid" :class="row.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </b-button>
          </template>

          <template #cell(createdAt)="row">
            {{ formatFecha(row.item.createdAt) }}
          </template>

          <template #cell(cliente)="row">
            <b-link
              v-if="row.item.cliente.id"
              class="font-weight-bold"
              :to="{ name: 'customer-profile', params: { id: row.item.cliente.id } }"
            >
              {{ row.item.cliente.nombre || '—' }}
            </b-link>
            <span
              v-else
              class="font-weight-bold"
            >{{ row.item.cliente.nombre || '—' }}</span>
          </template>

          <template #cell(email)="row">
            {{ row.item.cliente.email || '—' }}
          </template>

          <template #cell(uds)="row">
            {{ totalUds(row.item) }}
          </template>

          <template #cell(ref)="row">
            <span class="font-weight-bold">{{ row.item.ref }}</span>
            <b-badge
              v-if="esWeb(row.item)"
              variant="light-info"
              class="d-block mt-25"
              title="Pedido creado por el cliente desde la web wholesale"
            ><i class="fa-solid fa-globe mr-25"></i>WEB</b-badge>
            <b-badge
              v-if="row.item.cancelado"
              variant="light-danger"
              class="d-block mt-25"
              :title="`Motivo: ${row.item.canceladoMotivo || 'sin motivo'}`"
            >CANCELADO</b-badge>
          </template>

          <template #cell(total)="row">
            <span class="font-weight-bold">{{ row.item.simbolo }}{{ row.item.total }}</span>
          </template>

          <template #cell(emailEnviado)="row">
            <b-badge :variant="row.item.emailEnviado ? 'light-success' : 'light-secondary'">
              {{ row.item.emailEnviado ? 'Enviado' : 'No' }}
            </b-badge>
          </template>

          <template #cell(estado)="row">
            <b-badge
              v-if="row.item.cancelado"
              variant="light-danger"
              class="esc-pago-badge"
              title="Cambiar el estado reactiva el pedido"
              @click.stop="abrirMenuEstado(row.item, $event)"
            >
              <i class="fa-solid fa-ban mr-50"></i>Cancelled <i class="fa-solid fa-caret-down ml-25"></i>
            </b-badge>
            <b-badge
              v-else
              :variant="estadoVariant(row.item.estado)"
              class="esc-pago-badge"
              @click.stop="abrirMenuEstado(row.item, $event)"
            >
              <i :class="estadoIcono(row.item.estado)" class="mr-50"></i>{{ row.item.estado }} <i class="fa-solid fa-caret-down ml-25"></i>
            </b-badge>
            <small
              v-if="row.item.tracking"
              class="text-muted d-block mt-25"
              :title="row.item.trackingCompany"
            >
              <i class="fa-solid fa-truck-fast mr-25"></i>{{ row.item.tracking }}
            </small>
          </template>

          <template #cell(proforma)="row">
            <template v-if="row.item.proforma && row.item.proforma.n">
              <b-badge
                :variant="row.item.proforma.importe >= totalCobrar(row.item) ? 'light-success' : 'light-warning'"
                class="esc-pago-badge"
                title="Click para gestionar las proformas"
                @click="$refs.proformaModal.abrir(row.item)"
              >
                {{ row.item.proforma.n }} proforma{{ row.item.proforma.n === 1 ? '' : 's' }}
              </b-badge>
              <small class="text-muted d-block">
                {{ row.item.simbolo }}{{ fmtImporte(row.item.proforma.importe) }} / {{ row.item.simbolo }}{{ fmtImporte(totalCobrar(row.item)) }}
              </small>
            </template>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(pago)="row">
            <b-badge
              :variant="estadoPago(row.item).variant"
              class="esc-pago-badge"
              @click="abrirPagos(row.item)"
            >
              {{ estadoPago(row.item).text }}
            </b-badge>
            <small v-if="Number(row.item.pagado)" class="text-muted d-block">
              {{ row.item.simbolo }}{{ fmtImporte(row.item.pagado) }} / {{ row.item.simbolo }}{{ fmtImporte(totalCobrar(row.item)) }}
            </small>
          </template>

          <template #cell(acciones)="row">
            <!-- Todas las acciones agrupadas en un único menú -->
            <b-dropdown
              class="esc-acciones__menu"
              size="sm"
              variant="outline-secondary"
              right
              no-caret
              boundary="window"
              :popper-opts="{ positionFixed: true }"
              toggle-class="btn-icon"
            >
              <template #button-content>
                <i class="fa-solid fa-ellipsis-vertical"></i>
              </template>

              <b-dropdown-item @click="$refs.proformaModal.abrir(row.item)">
                <i class="fa-solid fa-file-invoice mr-50 text-info"></i> Factura proforma
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="pdfPedidoId === row.item.id"
                @click="verPdfPedido(row.item)"
              >
                <i class="fa-solid fa-file-pdf mr-50 text-secondary"></i> Ver PDF
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!row.item.cliente.email || reenviandoId === row.item.id"
                @click="reenviar(row.item)"
              >
                <i class="fa-solid fa-paper-plane mr-50 text-success"></i>
                {{ row.item.cliente.email ? 'Reenviar email' : 'Sin email' }}
              </b-dropdown-item>
              <b-dropdown-item @click="abrirEditar(row.item)">
                <i class="fa-solid fa-pen mr-50 text-primary"></i> Editar pedido
              </b-dropdown-item>
              <b-dropdown-item @click="abrirAlbaran(row.item)">
                <i class="fa-solid fa-truck-fast mr-50"></i> Albarán
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'escaner-confirmacion', params: { id: row.item.id } }">
                <i class="fa-solid fa-box-open mr-50"></i> Confirmation invoice
              </b-dropdown-item>
              <b-dropdown-item @click="$refs.comentariosModal.abrir(row.item)">
                <i class="fa-solid fa-comments mr-50 text-info"></i> Comentarios
              </b-dropdown-item>
              <b-dropdown-item @click="$refs.incidenciasModal.abrir(row.item)">
                <i class="fa-solid fa-triangle-exclamation mr-50 text-warning"></i> Incidencias
              </b-dropdown-item>
              <b-dropdown-item @click="verHistorial(row.item)">
                <i class="fa-solid fa-clock-rotate-left mr-50 text-secondary"></i> Historial
              </b-dropdown-item>
              <template v-if="!row.item.cancelado">
                <b-dropdown-divider class="esc-acciones__full" />
                <b-dropdown-item class="esc-acciones__full" variant="danger" @click="eliminar(row.item)">
                  <i class="fa-solid fa-ban mr-50"></i> Cancelar pedido
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </template>

          <!-- Desglose de artículos del pedido -->
          <template #row-details="row">
            <div v-if="row.item.cancelado" class="text-danger mb-50">
              <i class="fa-solid fa-ban mr-25"></i>
              <b>Pedido cancelado.</b> Motivo: {{ row.item.canceladoMotivo || 'sin motivo' }}
            </div>
            <div class="esc-det">
              <div
                v-for="(item, idx) in row.item.items"
                :key="item.sku + '_' + idx"
                class="esc-det-item"
              >
                <div class="esc-det-thumb">
                  <img v-if="item.image" :src="item.image" />
                  <i v-else class="fa-solid fa-image text-muted"></i>
                </div>
                <div class="esc-det-info">
                  <div class="esc-det-title">{{ item.title }}</div>
                  <div class="esc-det-sub">
                    <span v-if="item.color" class="esc-det-color">{{ item.color }}</span>
                    <code class="esc-det-sku">{{ item.sku }}</code>
                  </div>
                </div>
                <b-badge variant="light-secondary" class="esc-det-qty">x{{ item.qty }}</b-badge>
                <div class="esc-det-price">
                  <div class="esc-det-unit">{{ row.item.simbolo }}{{ item.wspUnit }}</div>
                  <small class="text-muted">{{ row.item.simbolo }}{{ lineaTotal(item) }}</small>
                </div>
              </div>
            </div>

          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <!-- Menú de estado (dropdown propio: posición fija, sin estilos de Bootstrap) -->
    <div
      v-if="estadoMenu.pedido"
      class="esc-dd"
      :style="{ top: estadoMenu.y + 'px', left: estadoMenu.x + 'px' }"
      @click.stop
    >
      <div
        v-for="e in estados"
        :key="e"
        class="esc-dd-item"
        :class="{ 'esc-dd-item--activo': e === estadoMenu.pedido.estado }"
        @click="elegirEstado(e)"
      >
        <i :class="estadoIcono(e)" class="mr-50"></i>{{ e }}
      </div>
      <div v-if="!estadoMenu.pedido.cancelado" class="esc-dd-item" @click="elegirEstado('Cancelled')">
        <i class="fa-solid fa-ban text-danger mr-50"></i>Cancelled
      </div>
    </div>

    <!-- Modal de edición -->
    <b-modal
      v-model="showEdit"
      title="Editar pedido"
      size="lg"
      scrollable
      :ok-disabled="guardando || !editando || editando.items.length === 0"
      :ok-title="guardando ? 'Guardando…' : 'Guardar cambios'"
      cancel-title="Cancelar"
      @ok.prevent="guardar"
    >
      <div v-if="editando">
        <!-- Datos del cliente -->
        <h6 class="font-weight-bold mb-1"><i class="fa-solid fa-user mr-50 text-primary"></i> Cliente</h6>
        <b-row>
          <b-col cols="12">
            <b-form-group label="Company name" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.empresa" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Name" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.nombre" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Surname" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.apellidos" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="NIF" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.nif" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="VAT number" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.vat" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Email" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.email" type="email" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Teléfono" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.telefono" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Dirección" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.direccion" />
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="C.P." label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.cp" />
            </b-form-group>
          </b-col>
          <b-col cols="8">
            <b-form-group label="Ciudad" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.ciudad" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="País" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.pais" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Web" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.web" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Social media" label-class="small font-weight-bold">
              <b-form-input v-model="editando.cliente.redes" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Notas" label-class="small font-weight-bold">
              <b-form-textarea v-model="editando.cliente.notas" rows="2" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Observaciones" label-class="small font-weight-bold">
              <b-form-textarea v-model="editando.cliente.observaciones" rows="2" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <hr class="my-1" />
            <b-form-checkbox v-model="editando.envio.activo" switch>
              <span class="font-weight-bold small">Dirección de envío diferente</span>
            </b-form-checkbox>
          </b-col>
          <template v-if="editando.envio.activo">
            <b-col cols="12" md="6">
              <b-form-group label="Nombre (envío)" label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.nombre" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Empresa (envío)" label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.empresa" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Dirección (envío)" label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.direccion" />
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group label="C.P." label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.cp" />
              </b-form-group>
            </b-col>
            <b-col cols="8">
              <b-form-group label="Ciudad" label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.ciudad" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="País" label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.pais" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Teléfono (envío)" label-class="small font-weight-bold">
                <b-form-input v-model="editando.envio.telefono" />
              </b-form-group>
            </b-col>
          </template>
        </b-row>

        <!-- Items -->
        <h6 class="font-weight-bold mb-1 mt-1"><i class="fa-solid fa-basket-shopping mr-50 text-primary"></i> Artículos</h6>
        <div v-if="editando.items.length === 0" class="text-muted text-center py-2">
          El pedido no tiene artículos
        </div>
        <div v-for="(item, idx) in editando.items" :key="item.sku + '_' + idx" class="esc-edit-item">
          <div class="esc-edit-thumb">
            <img v-if="item.image" :src="item.image" />
            <i v-else class="fa-solid fa-image text-muted"></i>
          </div>
          <div class="esc-edit-info">
            <div class="font-weight-bold">{{ item.title }}</div>
            <span class="text-muted small">{{ item.color }}</span>
            <code class="d-block small">{{ item.sku }}</code>
          </div>
          <b-input-group size="sm" class="esc-edit-qty">
            <b-input-group-prepend>
              <b-button variant="outline-secondary" @click="decQty(item)"><i class="fa-solid fa-minus"></i></b-button>
            </b-input-group-prepend>
            <b-form-input v-model.number="item.qty" type="number" min="1" class="text-center" @change="normalizarQty(item)" />
            <b-input-group-append>
              <b-button variant="outline-secondary" @click="incQty(item)"><i class="fa-solid fa-plus"></i></b-button>
            </b-input-group-append>
          </b-input-group>
          <div class="esc-edit-line">
            {{ editando.simbolo }}{{ lineaTotal(item) }}
          </div>
          <b-button variant="flat-danger" size="sm" class="btn-icon" @click="quitarItem(idx)">
            <i class="fa-solid fa-xmark"></i>
          </b-button>
        </div>

        <!-- Agregar producto -->
        <div class="esc-add-box mt-1">
          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <i class="fa-solid fa-barcode text-muted"></i>
            </b-input-group-prepend>
            <b-form-input
              v-model="codigoAdd"
              placeholder="Añadir producto: código/SKU y Enter"
              @keyup.enter="buscarProductoAdd"
            />
            <b-input-group-append>
              <b-button variant="primary" :disabled="buscandoAdd" @click="buscarProductoAdd">
                <b-spinner v-if="buscandoAdd" small />
                <i v-else class="fa-solid fa-magnifying-glass"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div v-if="productoAdd" class="esc-add-result mt-1">
            <div class="d-flex justify-content-between align-items-center mb-50">
              <span class="font-weight-bold small">{{ productoAdd.title }}</span>
              <b-button size="sm" variant="flat-secondary" class="btn-icon" @click="productoAdd = null">
                <i class="fa-solid fa-xmark"></i>
              </b-button>
            </div>
            <div
              v-for="vari in productoAdd.variants"
              :key="vari.sku"
              class="esc-add-variant"
            >
              <div class="esc-edit-thumb">
                <img v-if="vari.image" :src="vari.image" />
                <i v-else class="fa-solid fa-image text-muted"></i>
              </div>
              <div class="esc-edit-info">
                <div class="font-weight-bold small">{{ vari.color }}</div>
                <code class="small">{{ vari.sku }}</code>
              </div>
              <div class="esc-add-price">{{ editando.simbolo }}{{ wspUnidad(vari.price) }}</div>
              <b-button size="sm" variant="primary" class="btn-icon" @click="agregarVarianteAdd(vari)">
                <i class="fa-solid fa-plus"></i>
              </b-button>
            </div>
          </div>
        </div>

        <!-- Totales -->
        <div class="esc-edit-totales mt-1">
          <div class="d-flex justify-content-between mb-50">
            <span class="text-muted">Subtotal</span>
            <span class="font-weight-bold">{{ editando.simbolo }}{{ editSubtotal }}</span>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-50">
            <span class="text-muted">Descuento</span>
            <b-input-group size="sm" append="%" style="width: 110px">
              <b-form-input v-model.number="editando.descuentoPct" type="number" min="0" max="100" class="text-right" />
            </b-input-group>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-50">
            <b-form-checkbox v-model="editando.vat0" switch>
              <span class="text-muted">Mostrar "0% VAT" en los PDF</span>
            </b-form-checkbox>
            <span v-if="editando.vat0" class="font-weight-bold text-muted">{{ editando.simbolo }}0.00</span>
          </div>
          <div class="mb-50">
            <b-form-checkbox v-model="editando.datosBanco" switch>
              <span class="text-muted">Añadir datos bancarios (Wise EUR) en los PDF</span>
            </b-form-checkbox>
          </div>
          <div class="d-flex justify-content-between esc-edit-total">
            <span class="font-weight-bold">Total</span>
            <span class="font-weight-bolder text-primary">{{ editando.simbolo }}{{ editTotal }}</span>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modal: PDF del pedido -->
    <b-modal
      v-model="mostrarPdf"
      title="Pedido (PDF)"
      size="lg"
      centered
      hide-footer
      body-class="p-0"
      @hide="cerrarPdf"
    >
      <div class="esc-pdf-wrap">
        <iframe v-if="pdfUrl" :src="pdfUrl" class="esc-pdf-frame" title="PDF del pedido"></iframe>
      </div>
      <div class="d-flex justify-content-center align-items-center p-1">
        <b-form-checkbox
          v-if="pdfPedido"
          v-model="pdfConBanco"
          switch
          class="mr-2"
          :disabled="pdfPedidoId !== null"
          @change="verPdfPedido(pdfPedido, $event)"
        >
          <span class="text-muted">Con datos bancarios</span>
          <b-spinner v-if="pdfPedidoId !== null" small class="ml-50" />
        </b-form-checkbox>
        <b-button variant="primary" @click="descargarPdfArchivo">
          <i class="fa-solid fa-download mr-50"></i> Descargar
        </b-button>
      </div>
    </b-modal>

    <!-- Gestión de pagos del pedido -->
    <pagos-pedido-modal ref="pagosModal" />

    <!-- Factura proforma -->
    <proforma-modal ref="proformaModal" />
    <incidencias-modal ref="incidenciasModal" />
    <comentarios-pedido-modal ref="comentariosModal" />

    <!-- Albarán: productos con cantidad editable, PDF sin precios (no se guarda) -->
    <b-modal
      v-model="albaran.visible"
      :title="`Albarán — ${(albaran.pedido && albaran.pedido.ref) || ''}`"
      centered
      scrollable
      :ok-title="albaran.generando ? 'Generando…' : 'Generar PDF'"
      :ok-disabled="albaran.generando || !albaran.items.some(i => Number(i.qty) > 0)"
      cancel-title="Cancelar"
      @ok.prevent="generarAlbaran"
    >
      <div v-for="(it, i) in albaran.items" :key="'alb' + i" class="alb-item">
        <div class="min-w-0">
          <div class="font-weight-bold">{{ it.title }}</div>
          <small class="text-muted">{{ it.sku }}</small>
        </div>
        <b-form-input
          v-model="it.qty"
          type="number"
          min="0"
          size="sm"
          class="alb-qty"
          @wheel.prevent
        />
      </div>
      <small class="text-muted d-block mt-50">Cantidad 0 = el producto no sale en el albarán.</small>
    </b-modal>

    <!-- Historial del pedido -->
    <b-modal
      v-model="historial.visible"
      :title="`Historial — ${(historial.pedido && historial.pedido.ref) || ''}`"
      centered
      scrollable
      ok-only
      ok-title="Cerrar"
      ok-variant="secondary"
    >
      <div v-if="historial.cargando" class="text-center text-muted my-1">
        <b-spinner small class="align-middle mr-50" /> Cargando…
      </div>
      <div v-else-if="!historial.logs.length" class="text-muted">Sin acciones registradas.</div>
      <div v-else>
        <div v-for="log in historial.logs" :key="log.id" class="esc-log">
          <div class="esc-log__meta">
            <span class="esc-log__fecha">{{ formatFechaHora(log.createdAt) }}</span>
            <span class="esc-log__usuario">{{ log.usuario || '—' }}</span>
          </div>
          <div>{{ log.accion }}</div>
        </div>
      </div>
    </b-modal>

    <!-- Motivo de cancelación del pedido -->
    <b-modal
      v-model="cancelacion.visible"
      title="Cancelar pedido"
      centered
      ok-variant="danger"
      ok-title="Cancelar pedido"
      cancel-title="Volver"
      :ok-disabled="!cancelacion.motivo.trim()"
      @ok="confirmarCancelacion"
    >
      <label class="d-block mb-50">Motivo de la cancelación</label>
      <b-form-textarea v-model="cancelacion.motivo" rows="2" placeholder="Ej: el cliente ha anulado el pedido" />
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import PagosPedidoModal from '@/components/PagosPedidoModal.vue'
import ProformaModal from '@/components/ProformaModal.vue'
import IncidenciasModal from '@/components/IncidenciasModal.vue'
import ComentariosPedidoModal from '@/components/ComentariosPedidoModal.vue'

export default {
  components: {
    PagosPedidoModal, ProformaModal, IncidenciasModal, ComentariosPedidoModal,
  },
  data() {
    return {
      pedidos: [],
      cargando: false,
      cancelacion: { visible: false, motivo: '', pedido: null },
      historial: {
        visible: false, cargando: false, pedido: null, logs: [],
      },
      albaran: {
        visible: false, generando: false, pedido: null, items: [],
      },
      q: '',
      anioFiltro: 'Todos',
      pagoFiltro: 'Todos',
      origenFiltro: 'Todos',
      showEdit: false,
      editando: null,
      guardando: false,
      reenviandoId: null,
      // Estados del pedido (en inglés: los ve el cliente en la web wholesale)
      estados: ['Pending', 'Accepted', 'To Ship', 'Shipped'],
      cambiandoEstadoId: null,
      // Menú de estado abierto (dropdown propio): pedido + posición en pantalla
      estadoMenu: { pedido: null, x: 0, y: 0 },
      // Vista de PDF
      pdfPedidoId: null,
      pdfPedido: null,
      pdfConBanco: false,
      mostrarPdf: false,
      pdfUrl: '',
      pdfNombre: 'pedido.pdf',
      // Añadir productos en la edición
      codigoAdd: '',
      buscandoAdd: false,
      productoAdd: null,
      // Tasas desde GBP (deben coincidir con utils/monedas.js del API y Escaner.vue)
      monedas: [
        { code: 'GBP', symbol: '£', rate: 1 },
        { code: 'EUR', symbol: '€', rate: 1.17 },
        { code: 'USD', symbol: '$', rate: 1.35 },
        { code: 'SGD', symbol: 'S$', rate: 1.73 },
      ],
      fields: [
        { key: 'toggle', label: '', class: 'esc-col-toggle' },
        { key: 'ref', label: 'Pedido', thStyle: { width: '80px' } },
        { key: 'createdAt', label: 'Fecha', class: 'text-nowrap' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'email', label: 'Email', class: 'esc-col-email' },
        { key: 'uds', label: 'Uds' },
        { key: 'total', label: 'Total' },
        { key: 'estado', label: 'Estado', class: 'text-nowrap' },
        { key: 'proforma', label: 'Proforma' },
        { key: 'pago', label: 'Pago', class: 'text-nowrap' },
        { key: 'emailEnviado', label: 'Email' },
        { key: 'acciones', label: 'Acciones', class: 'text-center text-nowrap' },
      ],
    }
  },
  computed: {
    anios() {
      const set = new Set()
      this.pedidos.forEach(p => {
        const d = new Date(p.createdAt)
        if (!Number.isNaN(d.getTime())) set.add(d.getFullYear())
      })
      return Array.from(set).sort((a, b) => b - a)
    },
    pedidosFiltrados() {
      let lista = this.pedidos
      if (this.anioFiltro !== 'Todos') {
        lista = lista.filter(p => new Date(p.createdAt).getFullYear() === this.anioFiltro)
      }
      if (this.pagoFiltro !== 'Todos') {
        const pagado = p => (Number(p.pagado) || 0) + 0.009 >= this.totalCobrar(p)
        lista = lista.filter(p => (this.pagoFiltro === 'Pagados' ? pagado(p) : !pagado(p)))
      }
      if (this.origenFiltro !== 'Todos') {
        lista = lista.filter(p => (this.origenFiltro === 'Web' ? this.esWeb(p) : !this.esWeb(p)))
      }
      return lista
    },
    // Los cancelados salen en la tabla pero no cuentan en los KPIs
    pedidosActivos() {
      return this.pedidosFiltrados.filter(p => !p.cancelado)
    },
    totalUnidadesKpi() {
      return this.pedidosActivos.reduce((acc, p) => acc + this.totalUds(p), 0)
    },
    totalProformas() {
      return this.pedidosActivos.reduce((acc, p) => acc + ((p.proforma && p.proforma.n) || 0), 0)
    },
    editSubtotal() {
      if (!this.editando) return 0
      return this.editando.items.reduce(
        (acc, it) => acc + (Number(it.wspUnit) || 0) * (Number(it.qty) || 0), 0,
      )
    },
    editDescuentoPct() {
      if (!this.editando) return 0
      let p = parseFloat(this.editando.descuentoPct) || 0
      if (p < 0) p = 0
      if (p > 100) p = 100
      return p
    },
    editTotal() {
      return Math.ceil(this.editSubtotal * (1 - this.editDescuentoPct / 100))
    },
    rateActual() {
      if (!this.editando) return 1
      const m = this.monedas.find(x => x.code === this.editando.moneda)
      return m ? m.rate : 1
    },
  },
  created() {
    axios.post('/uso', { seccion: 'escaner-pedidos' }).catch(() => {})
    this.cargar()
  },
  mounted() {
    document.addEventListener('click', this.cerrarMenuEstado)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.cerrarMenuEstado)
    if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl)
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Pedidos',
        variant,
        solidColor: true,
      })
    },
    formatFecha(f) {
      if (!f) return ''
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES')
    },
    // Importe sin decimales si son .00, con 2 decimales si no (1480, 931.5 -> 931.50)
    fmtImporte(n) {
      const v = Math.round((Number(n) || 0) * 100) / 100
      return Number.isInteger(v) ? String(v) : v.toFixed(2)
    },
    // Suma un importe por pedido convertido todo a euros, usando las mismas
    // tasas desde GBP que usa el escáner (this.monedas)
    sumaEnEuros(fn) {
      const porSimbolo = { '£': 'GBP', '€': 'EUR', '$': 'USD', 'S$': 'SGD' }
      let totalGbp = 0
      this.pedidosActivos.forEach(p => {
        const code = p.moneda || porSimbolo[p.simbolo || '£'] || 'GBP'
        const m = this.monedas.find(x => x.code === code)
        totalGbp += (Number(fn(p)) || 0) / ((m && m.rate) || 1)
      })
      const eur = this.monedas.find(x => x.code === 'EUR')
      const total = totalGbp * ((eur && eur.rate) || 1)
      return `€${Math.round(total * 100) / 100}`
    },
    // Suma un importe por pedido agrupando por moneda (ej. "£1200 + €300")
    sumaPorMoneda(fn) {
      const porMoneda = {}
      this.pedidosActivos.forEach(p => {
        const s = p.simbolo || '£'
        porMoneda[s] = (porMoneda[s] || 0) + (Number(fn(p)) || 0)
      })
      return Object.keys(porMoneda)
        .filter(s => porMoneda[s] > 0)
        .map(s => `${s}${Math.round(porMoneda[s] * 100) / 100}`)
        .join(' + ')
    },
    totalUds(pedido) {
      return (pedido.items || []).reduce((acc, it) => acc + (Number(it.qty) || 0), 0)
    },
    // Pedido creado por el cliente desde la web wholesale B2B
    esWeb(pedido) {
      return pedido.usuarioNombre === 'Web B2B'
    },
    lineaTotal(item) {
      return String((Number(item.wspUnit) || 0) * (Number(item.qty) || 0))
    },
    cargar() {
      this.cargando = true
      axios
        .get('/escaner/pedidos', { params: { q: this.q || undefined } })
        .then(({ data }) => { this.pedidos = data })
        .catch(() => { this.notificar('No se pudieron cargar los pedidos', 'danger') })
        .finally(() => { this.cargando = false })
    },
    // Total a cobrar del pedido: productos + envío cobrado en proformas/facturas
    totalCobrar(pedido) {
      return Math.round(((Number(pedido.total) || 0) + (Number(pedido.envioCobrado) || 0)) * 100) / 100
    },
    estadoVariant(estado) {
      return {
        Pending: 'light-secondary', Accepted: 'light-info', 'To Ship': 'light-warning', Shipped: 'light-success',
      }[estado] || 'light-secondary'
    },
    abrirMenuEstado(pedido, ev) {
      if (this.estadoMenu.pedido && this.estadoMenu.pedido.id === pedido.id) {
        this.cerrarMenuEstado()
        return
      }
      const r = ev.currentTarget.getBoundingClientRect()
      this.estadoMenu = { pedido, x: r.left, y: r.bottom + 4 }
    },
    cerrarMenuEstado() {
      if (this.estadoMenu.pedido) this.estadoMenu = { pedido: null, x: 0, y: 0 }
    },
    elegirEstado(estado) {
      const pedido = this.estadoMenu.pedido
      this.cerrarMenuEstado()
      if (!pedido) return
      if (estado === 'Cancelled') this.eliminar(pedido)
      else this.cambiarEstado(pedido, estado)
    },
    estadoIcono(estado) {
      return {
        Pending: 'fa-solid fa-clock text-secondary',
        Accepted: 'fa-solid fa-circle-check text-info',
        'To Ship': 'fa-solid fa-boxes-packing text-warning',
        Shipped: 'fa-solid fa-truck-fast text-success',
      }[estado] || ''
    },
    cambiarEstado(pedido, estado) {
      if (estado === pedido.estado) return
      this.cambiandoEstadoId = pedido.id
      axios
        .put(`/escaner/pedidos/${pedido.id}/estado`, { estado })
        .then(() => {
          const reactivado = pedido.cancelado
          pedido.estado = estado
          // Cambiar el estado de un cancelado lo reactiva (igual que hace el backend)
          pedido.cancelado = false
          pedido.canceladoMotivo = ''
          this.notificar(reactivado ? `Pedido reactivado — ${estado}` : `Estado cambiado a ${estado}`)
        })
        .catch(() => { this.notificar('No se pudo cambiar el estado', 'danger') })
        .finally(() => { this.cambiandoEstadoId = null })
    },
    estadoPago(pedido) {
      const total = this.totalCobrar(pedido)
      const pagado = Number(pedido.pagado) || 0
      if (pagado <= 0) return { text: 'Pendiente', variant: 'light-secondary' }
      if (pagado + 0.009 >= total) return { text: 'Pagado', variant: 'light-success' }
      return { text: 'Parcial', variant: 'light-warning' }
    },
    abrirPagos(pedido) {
      this.$refs.pagosModal.abrir(pedido)
    },
    abrirEditar(pedido) {
      this.editando = JSON.parse(JSON.stringify(pedido))
      if (!Array.isArray(this.editando.items)) this.editando.items = []
      // Normaliza el envío para que el formulario sea reactivo en pedidos antiguos
      this.editando.envio = Object.assign(
        { activo: false, empresa: '', nombre: '', telefono: '', direccion: '', cp: '', ciudad: '', pais: '' },
        this.editando.envio || {},
      )
      this.codigoAdd = ''
      this.productoAdd = null
      this.showEdit = true
    },
    // WSP unitario (40% del RRP, es decir 60% de descuento) en la moneda del pedido, redondeado arriba
    wspUnidad(priceGBP) {
      const base = (parseFloat(priceGBP) || 0) * 0.4 * this.rateActual
      return Math.ceil(base)
    },
    buscarProductoAdd() {
      const code = (this.codigoAdd || '').trim()
      if (!code) return
      this.buscandoAdd = true
      axios
        .get('/escaner/product', { params: { code } })
        .then(({ data }) => {
          if (!data || !data.found) {
            this.notificar('Producto no encontrado: ' + code, 'danger')
            this.productoAdd = null
            return
          }
          this.productoAdd = data
        })
        .catch(() => { this.notificar('No se pudo consultar Shopify', 'danger') })
        .finally(() => { this.buscandoAdd = false; this.codigoAdd = '' })
    },
    agregarVarianteAdd(vari) {
      const existente = this.editando.items.find(it => it.sku === vari.sku)
      if (existente) {
        existente.qty = (Number(existente.qty) || 1) + 1
      } else {
        const wsp = this.wspUnidad(vari.price)
        this.editando.items.push({
          sku: vari.sku,
          barcode: vari.barcode,
          title: this.productoAdd.title,
          color: vari.color,
          image: vari.image,
          price: vari.price,
          qty: 1,
          wspUnit: wsp,
          lineTotal: wsp,
        })
      }
      this.notificar(`${vari.color} añadido`)
    },
    incQty(item) {
      item.qty = Math.min(999, (parseInt(item.qty, 10) || 0) + 1)
    },
    decQty(item) {
      item.qty = Math.max(1, (parseInt(item.qty, 10) || 1) - 1)
    },
    normalizarQty(item) {
      let n = parseInt(item.qty, 10)
      if (!n || n < 1) n = 1
      if (n > 999) n = 999
      item.qty = n
    },
    quitarItem(idx) {
      this.editando.items.splice(idx, 1)
    },
    // Payload del pedido a partir del formulario de edición
    payloadEdit() {
      return {
        cliente: this.editando.cliente,
        envio: this.editando.envio,
        moneda: this.editando.moneda,
        simbolo: this.editando.simbolo,
        descuentoPct: this.editDescuentoPct,
        vat0: !!this.editando.vat0,
        datosBanco: !!this.editando.datosBanco,
        subtotal: this.editSubtotal,
        total: this.editTotal,
        items: this.editando.items.map(it => ({
          ...it,
          lineTotal: (Number(it.wspUnit) || 0) * (Number(it.qty) || 0),
        })),
      }
    },
    guardar() {
      if (!this.editando || this.editando.items.length === 0) return
      this.guardando = true
      axios
        .put(`/escaner/pedidos/${this.editando.id}`, this.payloadEdit())
        .then(() => {
          this.notificar('Pedido actualizado')
          this.showEdit = false
          this.cargar()
        })
        .catch(() => { this.notificar('No se pudo actualizar el pedido', 'danger') })
        .finally(() => { this.guardando = false })
    },
    // Payload común (cliente + envío + items) para PDF/email a partir de un pedido guardado
    payloadPedido(pedido) {
      return {
        cliente: pedido.cliente,
        envio: pedido.envio,
        moneda: pedido.moneda,
        simbolo: pedido.simbolo,
        subtotal: pedido.subtotal,
        descuentoPct: pedido.descuentoPct,
        vat0: !!pedido.vat0,
        datosBanco: !!pedido.datosBanco,
        descuento: String(Math.max(0, (Number(pedido.subtotal) || 0) - (Number(pedido.total) || 0))),
        total: pedido.total,
        items: pedido.items,
      }
    },
    // Genera y muestra el PDF de un pedido guardado (por id)
    // conBanco: si no se indica, se usa lo guardado en el pedido
    verPdfPedido(pedido, conBanco) {
      const banco = conBanco === undefined ? !!pedido.datosBanco : !!conBanco
      this.pdfPedidoId = pedido.id
      axios
        .get(`/escaner/pedidos/${pedido.id}/pdf`, { params: { banco: banco ? 1 : 0 }, responseType: 'blob' })
        .then(({ data }) => {
          this.pdfPedido = pedido
          this.pdfConBanco = banco
          if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl)
          this.pdfUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          const num = String(pedido.ref || '').replace(/\D/g, '') || pedido.id
          this.pdfNombre = `Order_Confirmacion_${num}.pdf`
          this.mostrarPdf = true
        })
        .catch(() => {
          // Si falla al cambiar el interruptor, se deja como estaba
          if (conBanco !== undefined) this.pdfConBanco = !banco
          this.notificar('No se pudo generar el PDF', 'danger')
        })
        .finally(() => { this.pdfPedidoId = null })
    },
    descargarPdfArchivo() {
      if (!this.pdfUrl) return
      const a = document.createElement('a')
      a.href = this.pdfUrl
      a.download = this.pdfNombre
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    cerrarPdf() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl)
        this.pdfUrl = ''
      }
    },
    // Reenvía el email de un pedido ya guardado (desde la lista)
    reenviar(pedido) {
      const email = ((pedido.cliente && pedido.cliente.email) || '').trim()
      if (!email) {
        this.notificar('El pedido no tiene email', 'danger')
        return
      }
      this.reenviandoId = pedido.id
      const payload = {
        ...this.payloadPedido(pedido),
        email,
        pedidoId: pedido.id,
      }
      axios
        .post('/escaner/pedido/email', payload)
        .then(() => {
          this.notificar(`Email reenviado a ${email}`)
          pedido.emailEnviado = true
        })
        .catch(() => { this.notificar('No se pudo reenviar el email', 'danger') })
        .finally(() => { this.reenviandoId = null })
    },
    abrirAlbaran(pedido) {
      this.albaran = {
        visible: true,
        generando: false,
        pedido,
        items: (pedido.items || []).map(it => ({ ...it, qty: parseInt(it.qty, 10) || 0 })),
      }
    },
    generarAlbaran() {
      const { pedido } = this.albaran
      const items = this.albaran.items.filter(it => Number(it.qty) > 0)
      this.albaran.generando = true
      axios
        .post('/escaner/pedido/pdf', {
          albaran: true,
          id: pedido.id,
          cliente: pedido.cliente,
          envio: pedido.envio,
          moneda: pedido.moneda,
          simbolo: pedido.simbolo,
          items,
        }, { responseType: 'blob' })
        .then(({ data }) => {
          if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl)
          this.pdfUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          const num = String(pedido.ref || '').replace(/\D/g, '') || pedido.id
          this.pdfNombre = `Albaran_${num}.pdf`
          this.pdfPedido = null
          this.albaran.visible = false
          this.mostrarPdf = true
        })
        .catch(() => { this.notificar('No se pudo generar el albarán', 'danger') })
        .finally(() => { this.albaran.generando = false })
    },
    formatFechaHora(f) {
      if (!f) return '--'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })
    },
    verHistorial(pedido) {
      this.historial = {
        visible: true, cargando: true, pedido, logs: [],
      }
      axios
        .get(`/escaner/pedidos/${pedido.id}/logs`)
        .then(({ data }) => { this.historial.logs = data || [] })
        .catch(() => { this.notificar('No se pudo cargar el historial', 'danger') })
        .finally(() => { this.historial.cargando = false })
    },
    filaClase(item, type) {
      return type === 'row' && item && item.cancelado ? 'esc-row-cancelada' : ''
    },
    eliminar(pedido) {
      // Pedido protegido: con facturas o pagos no se llega ni a llamar a la API
      if (Number(pedido.nFacturas) > 0 || Number(pedido.pagado) > 0) {
        const motivo = Number(pedido.nFacturas) > 0 ? 'tiene facturas emitidas' : 'tiene pagos registrados'
        this.$bvModal.msgBoxOk(`No se puede cancelar: el pedido ${motivo}.`, {
          title: 'Pedido protegido', okTitle: 'Entendido', centered: true,
        })
        return
      }
      this.cancelacion = { visible: true, motivo: '', pedido }
    },
    confirmarCancelacion() {
      const { pedido, motivo } = this.cancelacion
      axios
        .delete(`/escaner/pedidos/${pedido.id}`, { data: { motivo: motivo.trim() } })
        .then(() => {
          this.notificar('Pedido cancelado')
          this.cargar()
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo cancelar el pedido'
          this.notificar(msg, 'danger')
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.esc-pdf-wrap {
  width: 100%;
  height: 75vh;
  background: #525659;
}
.esc-pdf-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.esc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 1rem;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  }
}

.esc-edit-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f2f7;

  &:last-of-type { border-bottom: none; }
}

.esc-edit-thumb {
  width: 44px; height: 44px; flex: 0 0 44px;
  border-radius: 8px; background: #f8f8fc; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
}

.esc-edit-info { flex: 1 1 auto; min-width: 0; }
.esc-edit-qty { width: 120px; flex: 0 0 120px; }
.esc-edit-line { width: 70px; text-align: right; font-weight: 700; }

/* --- Columna y botón de desplegar --- */
// Los ::v-deep van bajo .esc-pedidos-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.esc-pedidos-page ::v-deep .esc-col-toggle {
  width: 28px;
  text-align: center;
  padding-left: 0.25rem;
  padding-right: 0;
}

.esc-toggle-btn {
  transition: color 0.15s;
  &.active { color: #7367f0; }
}

/* --- Desglose de artículos en la tabla --- */
.esc-pedidos-page ::v-deep tr.b-table-details > td { padding: 0 !important; }

.esc-det {
  padding: 0.5rem 0.75rem 0.75rem;
}

.esc-det-item {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.6rem 0.85rem;
  background: #fff;
  border: 1px solid #f0eef7;
  border-radius: 10px;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.04);
  max-width: 620px;

  &:last-child { margin-bottom: 0; }
}

.esc-det-thumb {
  width: 50px; height: 50px; flex: 0 0 50px;
  border-radius: 8px; background: #f8f8fc; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
}

.esc-det-info { flex: 1 1 auto; min-width: 0; }
.esc-det-title {
  font-weight: 600; font-size: 0.9rem; color: #2b2b2b;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.esc-det-sub { display: flex; align-items: center; gap: 0.5rem; margin-top: 3px; }
.esc-det-color { font-size: 0.78rem; color: #6e6b7b; }
.esc-det-sku {
  font-size: 0.7rem;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
  border-radius: 4px;
  padding: 1px 7px;
}

.esc-det-qty { flex: 0 0 auto; font-size: 0.8rem; }
.esc-det-price { flex: 0 0 auto; min-width: 74px; text-align: right; }
.esc-det-unit { font-weight: 700; color: #2b2b2b; }

.esc-add-box {
  background: #f8f8fc;
  border: 1px dashed #d8d4f7;
  border-radius: 10px;
  padding: 0.75rem;
}

.esc-add-result {
  max-height: 220px;
  overflow-y: auto;
}

.esc-add-variant {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #ece9fb;

  &:last-child { border-bottom: none; }
}

.esc-add-price {
  font-weight: 700;
  min-width: 56px;
  text-align: right;
}

.esc-edit-totales {
  border-top: 1px solid #ebe9f1;
  padding-top: 0.75rem;
}
.esc-edit-total {
  border-top: 1px solid #ebe9f1;
  padding-top: 0.5rem;
  font-size: 1.15rem;
}

@media (max-width: 575px) {
  .esc-edit-item { flex-wrap: wrap; }
  .esc-edit-qty { order: 3; }
  .esc-edit-line { order: 4; margin-left: auto; }
}

// Pagos
// Dropdown de estado hecho a mano: clases propias, cero estilos de Bootstrap
.esc-dd {
  position: fixed;
  z-index: 1060;
  min-width: 170px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  box-shadow: 0 5px 25px rgba(34, 41, 47, 0.18);
  padding: 4px;
}

.esc-dd-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.95rem;
  color: #6e6b7b;

  &:hover {
    background: #f2f2f7;
  }

  // Estado actual: fondo morado sutil + negrita (los iconos conservan su color)
  &.esc-dd-item--activo,
  &.esc-dd-item--activo:hover {
    background: rgba(115, 103, 240, 0.1);
    font-weight: 600;
  }
}

.esc-pago-badge {
  cursor: pointer;
}

/* --- Acciones: siempre agrupadas en un único menú --- */
.esc-acciones__menu {
  display: inline-block;
}
// El menú es largo: positionFixed (en el template) lo saca del recorte de la tabla responsive,
// va en 2 columnas para que sea más bajo y, si aun así no cabe, hace scroll en vez de cortarse
.esc-pedidos-page ::v-deep .esc-acciones__menu .dropdown-menu {
  max-height: calc(100vh - 20px);
  overflow-y: auto;

  &.show {
    display: grid;
    grid-template-columns: repeat(2, auto);
  }

  .esc-acciones__full {
    grid-column: 1 / -1;
  }
}

.esc-anios {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;

  &__sep {
    width: 1px;
    height: 22px;
    background: #ebe9f1;
    margin: 0 6px;
  }
}

.dark-layout .esc-anios__sep {
  background: #3b4253;
}

// KPIs
.esc-kpis {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.esc-kpi {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;

  &--ok { border-color: rgba(#28c76f, 0.4); }
  &--warn { border-color: rgba(#ff9f43, 0.5); }

  &__label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #6e6b7b;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: #333;
    margin: 4px 0 2px;

    &--sm { font-size: 15px; margin-top: 8px; }
  }

  &__foot {
    font-size: 10px;
    color: #6e6b7b;
  }
}

.dark-layout {
  .esc-kpi {
    background: #283046;
    border-color: #3b4253;

    &--ok { border-color: rgba(#28c76f, 0.4); }
    &--warn { border-color: rgba(#ff9f43, 0.5); }

    &__value { color: #d0d2d6; }
  }
}

@media (max-width: 992px) {
  .esc-kpis { grid-template-columns: repeat(2, 1fr); }
}

// Filas de pedidos cancelados: atenuadas (el badge CANCELADO queda visible)
.esc-pedidos-page ::v-deep .esc-row-cancelada > td { opacity: 0.55; }

// Albarán: filas de producto + cantidad
.alb-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #ebe9f1;

  &:last-of-type { border-bottom: none; }
}

.alb-qty { width: 80px; flex-shrink: 0; }

// Historial del pedido
.esc-log {
  padding: 8px 0;
  border-bottom: 1px solid #ebe9f1;
  font-size: 13px;

  &:last-child { border-bottom: none; }

  &__meta {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  &__fecha {
    font-family: monospace;
    font-size: 11px;
    color: #6e6b7b;
  }

  &__usuario {
    font-size: 11px;
    font-weight: 600;
    color: #6e6b7b;
  }
}
</style>
